/**
 * useGroups — ViewModel for /iam/groups.
 *
 * Composes shared usePaginatedList<T>. Adds create/update/remove
 * mutations and surfaces the screen_actions catalogue (rides
 * alongside list payload per the locked plan).
 */

import { useCallback } from 'react'
import { useAuth } from '../context/AuthContext'
import { APIROUTES, fillPath } from '../constants/routes.constants'
import type {
  CreateGroupInput,
  GroupDto,
  ScreenActionCatalogueRow,
  UpdateGroupInput
} from '../models/group.model'
import { mutateJson } from '../utils/api/mutateJson'
import { parseApiError } from '../utils/api/parseApiError'
import { usePaginatedList } from './usePaginatedList'

// Single-source-of-truth: src/models/group.model.ts.
// Per SOP 02 §"Mutation Patterns (LOCKED)" — DTO source rule.
export type {
  CreateGroupInput,
  GroupDto,
  GroupScreenGrant,
  ScreenActionCatalogueRow,
  UpdateGroupInput
} from '../models/group.model'

interface ListBody {
  readonly success: true
  readonly data: {
    readonly items: GroupDto[]
    readonly total: number
    readonly page: number
    readonly pageSize: number
    readonly screen_actions: ScreenActionCatalogueRow[]
  }
}

export interface UseGroupsReturn {
  readonly items: GroupDto[]
  readonly screenActions: ScreenActionCatalogueRow[]
  readonly total: number
  readonly page: number
  readonly pageSize: number
  readonly isLoading: boolean
  readonly error: string | null
  readonly setPage: (n: number) => void
  readonly reload: () => void
  readonly create: (input: CreateGroupInput) => Promise<{ ok: true } | { ok: false; field: string | null; message: string }>
  readonly update: (id: number, patch: UpdateGroupInput) => Promise<{ ok: true } | { ok: false; field: string | null; message: string }>
  readonly remove: (id: number) => Promise<{ ok: true } | { ok: false; message: string }>
}

export function useGroups(): UseGroupsReturn {
  const { authedFetch } = useAuth()

  const paged = usePaginatedList<GroupDto, ScreenActionCatalogueRow[]>({
    fetcher: useCallback(
      async (page, pageSize, signal) => {
        const url = `${APIROUTES.IAMGROUPS}?page=${page}&pageSize=${pageSize}`
        const res = await authedFetch(url, { signal })
        if (!res.ok) {
          const { message } = await parseApiError(res)
          throw new Error(message)
        }
        const body = (await res.json()) as ListBody
        return {
          items: body.data.items,
          total: body.data.total,
          extras: body.data.screen_actions
        }
      },
      [authedFetch]
    )
  })

  const create = useCallback(
    async (input: CreateGroupInput) => {
      const r = await mutateJson<GroupDto>(
        authedFetch,
        APIROUTES.IAMGROUPS,
        'POST',
        {
          name: input.name,
          description: input.description,
          is_active: input.is_active,
          grants: input.grants
        }
      )
      if (!r.ok) return r
      if (paged.page === 1) paged.insertItem(r.data)
      else paged.reload()
      return { ok: true as const }
    },
    [authedFetch, paged]
  )

  const update = useCallback(
    async (id: number, patch: UpdateGroupInput) => {
      const r = await mutateJson<GroupDto>(
        authedFetch,
        fillPath(APIROUTES.IAMGROUPITEM, { id: String(id) }),
        'PATCH',
        patch
      )
      if (!r.ok) return r
      paged.updateItem((it) => it.id === id, r.data)
      return { ok: true as const }
    },
    [authedFetch, paged]
  )

  const remove = useCallback(
    async (id: number) => {
      const r = await mutateJson<{ ok: true }>(
        authedFetch,
        fillPath(APIROUTES.IAMGROUPITEM, { id: String(id) }),
        'DELETE'
      )
      if (!r.ok) return { ok: false as const, message: r.message }
      paged.removeItem((it) => it.id === id)
      return { ok: true as const }
    },
    [authedFetch, paged]
  )

  return {
    items: paged.items,
    screenActions: paged.extras ?? [],
    total: paged.total,
    page: paged.page,
    pageSize: paged.pageSize,
    isLoading: paged.isLoading,
    error: paged.error,
    setPage: paged.setPage,
    reload: paged.reload,
    create,
    update,
    remove
  }
}
